import { useEffect, useState } from "react";
import { DatePicker } from "@/components/Planning/components/DatePicker";
import ApproximateTimingSelect from "@/components/Planning/components/ApproximateTimingSelect";
import WeekNumberSelect from "@/components/Planning/components/WeekNumberSelect";
import SeasonsSelect from "@/components/Planning/components/SeasonsSelect";
import CharCount from "@/components/CharCount";
import { parseApproximateComponents } from "@/utils/taskLogic";
import { parseYMDToLocalDate, formatDateToYMD, TIMING_YEAR_START, TIMING_YEAR_END } from "@/utils/dateFormat";
import { DIARY_MAX_LENGTH } from "@/utils/trialFieldLimits";
import { expectedText, isIsoDate, timingMode } from "@/utils/planning";

// Planned dates are planning: from last year to 3 years ahead, as the other timing choices
const START_MONTH = new Date(TIMING_YEAR_START, 0, 1);
const END_MONTH = new Date(TIMING_YEAR_END, 11, 31);

const MODES = [
  { key: "date", label: "Exact date" },
  { key: "approx", label: "Month position" },
  { key: "week", label: "Week numbers" },
  { key: "season", label: "Seasons" },
  { key: "text", label: "Free text" },
];

// What each kind offers: the Planned value in five ways (one value), the Time interval as a date and/or text
const KINDS = {
  planned: {
    modes: MODES,
    groupLabel: "How the planned date is given",
    dateLabel: "Planned date",
    textLabel: "Planned (free text)",
    textMax: DIARY_MAX_LENGTH.planned_interval,
    placeholder: 'e.g. "after harvest"',
  },
  timeinterval: {
    modes: MODES.filter((m) => m.key === "date" || m.key === "text"),
    groupLabel: "How the time interval is given",
    dateLabel: "Time interval",
    textLabel: "Time interval (free text)",
    textMax: DIARY_MAX_LENGTH.timeinterval_text,
    placeholder: 'e.g. "BBCH 31-32" or "14 DAA"',
  },
};

/** Whether a mode can show the value ("" fits every mode; free text takes anything). */
function fitsMode(mode, value) {
  if (!value || mode === "text") return true;
  if (mode === "date") return isIsoDate(value);
  if (mode === "week") return parseApproximateComponents(value)?.mode === "week";
  return parseApproximateComponents(value)?.mode === "month"; // month position, season
}

/**
 * The timing of a task, with the same tabs and fields wherever it is given (Documentation/plans in agromate-api):
 * - kind "planned" (default): Agrolab's Planned value in any of five ways - an exact date, a month position, a week,
 *   a season or free text. `value` is the planned_interval as chosen ("" for none); onChange(value) gets the new
 *   one (its date: utils/planning.js plannedDateOf). A value the new tab cannot show is cleared.
 * - kind "timeinterval": the customer's Time interval as an exact date and/or free text. `value` is { date, text }
 *   ("YYYY-MM-DD" and words, "" for none); onChange({ date, text }). Switching tab clears nothing (a filled tab is
 *   marked), Clear clears the shown field only, and a line shows both as "<date> · <text>".
 * `dateLabel` names the date button (e.g. "Time interval for Visit 01"), `startMonth`/`endMonth` limit its calendar.
 * Touch-sized controls that wrap on a narrow screen.
 */
export default function PlannedInput({ value, onChange, idPrefix = "planned", kind = "planned", dateLabel, startMonth = START_MONTH, endMonth = END_MONTH }) {
  const k = KINDS[kind];
  const both = kind === "timeinterval";
  // The value each tab shows: one value for Planned, the date or the text for the Time interval
  const shownIn = (mode) => (both ? (mode === "date" ? value?.date : value?.text) || "" : value);
  const [mode, setMode] = useState(() => (both ? (value?.date || !value?.text ? "date" : "text") : timingMode(value)));

  // A value set from outside that the mode cannot show (e.g. the entry loaded after the form) shows its own mode
  useEffect(() => {
    if (!both && value && !fitsMode(mode, value)) setMode(timingMode(value));
  }, [both, value, mode]);

  const setShown = (next) => onChange(both ? { date: value?.date || "", text: value?.text || "", [mode === "date" ? "date" : "text"]: next } : next);

  const switchMode = (next) => {
    if (next === mode) return;
    // Planned: a value the new mode cannot show is cleared (the selects then fill in their own default)
    if (!both && !fitsMode(next, value)) onChange("");
    setMode(next);
  };

  const shown = shownIn(mode);
  // The free text as typed: a form may trim the value it holds, which would take a space typed last away (and so
  // the space between two words). The field keeps what was typed while it is the same text; it is trimmed on save.
  const [typed, setTyped] = useState("");
  const textShown = typed.trim() === String(shown).trim() ? typed : shown;
  const summary = both ? expectedText({ timeinterval: value?.date, timeinterval_text: value?.text }) : "";

  return (
    <div className="grid gap-2" data-testid={`${idPrefix}-input`}>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={k.groupLabel}>
        {k.modes.map((m) => {
          const filled = both && m.key !== mode && shownIn(m.key) !== "";
          return (
            <button
              key={m.key}
              type="button"
              aria-pressed={mode === m.key}
              onClick={() => switchMode(m.key)}
              title={filled ? `${m.label} is filled in` : undefined}
              className={`inline-flex min-h-10 items-center gap-1.5 rounded px-3 text-sm ${mode === m.key ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
            >
              {m.label}
              {filled && <span className="h-2 w-2 rounded-full bg-emerald-600 dark:bg-emerald-400" data-testid={`${idPrefix}-filled-${m.key}`} aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {mode === "date" && (
          <DatePicker
            selected={parseYMDToLocalDate(shown)}
            onChange={(date) => setShown(formatDateToYMD(date))}
            startMonth={startMonth}
            endMonth={endMonth}
            label={dateLabel ?? k.dateLabel}
          />
        )}
        {mode === "approx" && <ApproximateTimingSelect value={value} onChange={onChange} />}
        {mode === "week" && <WeekNumberSelect value={value} onChange={onChange} />}
        {mode === "season" && <SeasonsSelect value={value} onChange={onChange} />}
        {mode === "text" && (
          <div className="min-w-0 flex-1">
            <input
              id={`${idPrefix}-text`}
              aria-label={k.textLabel}
              value={textShown}
              onChange={(e) => {
                setTyped(e.target.value);
                setShown(e.target.value);
              }}
              maxLength={k.textMax}
              placeholder={k.placeholder}
              className="min-h-10 w-full rounded border px-3 py-2"
            />
            <CharCount value={textShown} max={k.textMax} />
          </div>
        )}
        {shown && (
          <button type="button" onClick={() => setShown("")} className="min-h-10 px-2 text-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400">
            Clear
          </button>
        )}
      </div>
      {summary && (
        <p className="text-sm text-gray-600 dark:text-gray-300" data-testid={`${idPrefix}-summary`}>{summary}</p>
      )}
    </div>
  );
}
